import React, { useMemo } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

/**
 * Phase RV — shared "Replacement value" money input.
 *
 * Single source of truth for the rental's equipment replacement value
 * across the three surfaces that set or edit it:
 *   1. Assign Equipment dialog          (POST /admin/submissions/{id}/assign-equipment)
 *   2. Import Rental ("Add current rental") dialog  (POST /admin/customers/{id}/import-rental)
 *   3. AdminRentalDetailPage inline edit (PATCH /admin/rentals/{id})
 *
 * Value semantics:
 *  - Accepts plain integer dollars (75000) or decimal (75000.00).
 *  - Returns a JS number rounded to whole dollars (the backend's
 *    canonical ``rental.value`` is an integer). Pass the value upstream
 *    via `onChange` as either a number or null.
 *  - Display formatting:
 *      75000   -> "$75,000"
 *      75000.5 -> "$75,000.50"
 *      null/"" -> empty (with the placeholder).
 *
 * Props
 *  value       number | null         current saved value (in dollars)
 *  onChange(v) (number | null) => …  fires when user edits; v is rounded
 *  required    bool                   adds the red asterisk
 *  testIdBase  string                 e.g. "ae-replacement-value"
 *  label       string                 defaults to "Replacement value ($)"
 *  hint        string|null            small caption under the field
 *  disabled    bool
 */
export function formatReplacementValue(n) {
    if (n == null || n === "") return "";
    const num = Number(n);
    if (!Number.isFinite(num) || num <= 0) return "";
    if (Math.abs(num - Math.round(num)) < 0.005) {
        return `$${Math.round(num).toLocaleString("en-US")}`;
    }
    return `$${num.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

export default function ReplacementValueInput({
    value,
    onChange,
    required = false,
    testIdBase = "replacement-value",
    label = "Replacement value ($)",
    hint = "Optional now; required before sending a COI request.",
    disabled = false,
}) {
    // Hold the raw text so the user can type "75000." mid-edit without
    // the input snapping back. We don't try to format-while-typing — the
    // pretty `$75,000` display lives in the read-only sibling below.
    const display = useMemo(() => formatReplacementValue(value), [value]);

    const handle = (e) => {
        const raw = (e.target.value || "").replace(/[^0-9.]/g, "");
        if (raw === "" || raw === ".") { onChange(null); return; }
        const num = parseFloat(raw);
        if (!Number.isFinite(num) || num < 0) { onChange(null); return; }
        // Round to integer dollars — matches the rental.value schema
        // (Phase H.7) and what the rest of the app expects.
        onChange(Math.round(num));
    };

    return (
        <div className="space-y-1.5">
            <Label>
                {label}
                {required && <span className="text-red-500 ml-1">*</span>}
            </Label>
            <Input
                type="text"
                inputMode="decimal"
                value={value == null || value === "" ? "" : String(value)}
                onChange={handle}
                placeholder="e.g. 75000"
                disabled={disabled}
                data-testid={testIdBase}
            />
            {display && (
                <p className="text-[11px] text-slate-500" data-testid={`${testIdBase}-formatted`}>
                    {display}
                </p>
            )}
            {hint && !display && (
                <p className="text-[11px] text-slate-500" data-testid={`${testIdBase}-hint`}>{hint}</p>
            )}
        </div>
    );
}
